import { useMemo } from "react";

import { useSelector } from "react-redux";
import { useMemoizedKeyMap } from "@bridgesplit/ui";

import { MarketInformationsFilter, ParsedMarketInformationWithLtvInfo } from "../../types";
import { selectMarketInformationsCacheState } from "../slices";
import { useAbfTypesToUiConverter } from "../../utils";

interface OracleMarketInformationsCacheQueryResult {
    data: Map<string, ParsedMarketInformationWithLtvInfo> | undefined;
    isLoading: boolean;
    isFetching: boolean;
    allMarkets: ParsedMarketInformationWithLtvInfo[] | undefined;
}

export function useOracleMarketInformationsCacheQuery({
    verified,
    principalMints,
    addresses
}: MarketInformationsFilter): OracleMarketInformationsCacheQueryResult {
    // allMarkets here is the *full* cache of markets, always fetched at app load.
    const { byId: allMarkets, loading, isFetching } = useSelector(selectMarketInformationsCacheState);

    const { convertMarketInformationWithLtvInfo } = useAbfTypesToUiConverter();

    // Convert markets in the cache. Keep serving cached markets while any scoped fetch is in flight: that
    // fetch only merges into the cache, and blanking here would make every market consumer in the app read
    // undefined (e.g. dropping vaults whose ltvInfos depend on market info) whenever a new filter is queried.
    const allConvertedMarkets = useMemo(() => {
        if (!allMarkets || loading) return undefined;
        return allMarkets.map(convertMarketInformationWithLtvInfo);
    }, [allMarkets, convertMarketInformationWithLtvInfo, loading]);

    // Filter the cached and converted markets according to the input filter.
    const filteredMarkets = useMemo(() => {
        if (!allConvertedMarkets) return undefined;

        return allConvertedMarkets.filter((market) => {
            // Filter by principalMints if provided and not empty
            if (principalMints.length > 0 && !principalMints.includes(market.marketInformation.principalMint)) {
                return false;
            }

            // Filter by addresses if provided and not empty
            if (addresses.length > 0 && !addresses.includes(market.marketInformation.address)) {
                return false;
            }

            if (verified !== undefined && market.marketInformation.verified !== verified) {
                return false;
            }

            return true;
        });
    }, [allConvertedMarkets, verified, principalMints, addresses]);

    const markets = useMemoizedKeyMap<ParsedMarketInformationWithLtvInfo, string>(
        filteredMarkets,
        (market) => market.marketInformation.address
    );

    return {
        data: markets,
        isLoading: loading,
        isFetching,
        allMarkets: filteredMarkets ?? undefined
    };
}
